import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminScreen } from '../api.js';
import type { ToastMessage } from '../types.js';

export const SUCCESS_TOAST_TTL_MS = 10_000;

type ScreenToast = ToastMessage & { id: number };

export function useAdminToasts(screen: AdminScreen) {
  const [toastByScreen, setToastByScreen] = useState<Partial<Record<AdminScreen, ScreenToast>>>({});
  const screenRef = useRef<AdminScreen>(screen);
  const toastSeq = useRef(0);
  const successToastTimers = useRef<Partial<Record<AdminScreen, number>>>({});

  useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  const clearSuccessToastTimer = useCallback((targetScreen: AdminScreen) => {
    const timer = successToastTimers.current[targetScreen];
    if (timer !== undefined) window.clearTimeout(timer);
    delete successToastTimers.current[targetScreen];
  }, []);

  const clearAllToasts = useCallback(() => {
    for (const timer of Object.values(successToastTimers.current)) {
      if (timer !== undefined) window.clearTimeout(timer);
    }
    successToastTimers.current = {};
    setToastByScreen({});
  }, []);

  const dismissToast = useCallback(
    (targetScreen: AdminScreen) => {
      clearSuccessToastTimer(targetScreen);
      setToastByScreen((current) => {
        const next = { ...current };
        delete next[targetScreen];
        return next;
      });
    },
    [clearSuccessToastTimer]
  );

  const showToastForScreen = useCallback(
    (targetScreen: AdminScreen, toast: ToastMessage) => {
      clearSuccessToastTimer(targetScreen);
      const id = ++toastSeq.current;
      setToastByScreen((current) => ({
        ...current,
        [targetScreen]: { ...toast, id }
      }));
      if (toast.tone !== 'success') return;
      successToastTimers.current[targetScreen] = window.setTimeout(() => {
        setToastByScreen((current) => {
          if (current[targetScreen]?.id !== id) return current;
          const next = { ...current };
          delete next[targetScreen];
          return next;
        });
        delete successToastTimers.current[targetScreen];
      }, SUCCESS_TOAST_TTL_MS);
    },
    [clearSuccessToastTimer]
  );

  const showToast = useCallback(
    (toast: ToastMessage) => showToastForScreen(screenRef.current, toast),
    [showToastForScreen]
  );

  const onToastForScreen = useCallback(
    (toast: ToastMessage) => showToastForScreen(screen, toast),
    [screen, showToastForScreen]
  );

  useEffect(
    () => () => {
      for (const timer of Object.values(successToastTimers.current)) {
        if (timer !== undefined) window.clearTimeout(timer);
      }
    },
    []
  );

  return {
    toastByScreen,
    clearAllToasts,
    dismissToast,
    showToast,
    onToastForScreen
  };
}
